import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../config/api';
import styles from './PointsHistoryModal.module.css';

interface PointsTransaction {
  id: string;
  points: number;
  featureType: string;
  description: string;
  balanceAfter: number;
  createdAt: string;
}

interface PointsHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PointsHistoryModal: React.FC<PointsHistoryModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [transactions, setTransactions] = useState<PointsTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      loadTransactions();
    }
  }, [isOpen]);

  const loadTransactions = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(getApiUrl('/usage/points/history?limit=50'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load transaction history');
      }

      const data = await response.json();
      console.log('Points transaction data:', data);
      
      // Ensure transactions is always an array
      const transactionsData = data.transactions || data || [];
      console.log('Processed transactions:', transactionsData);
      
      setTransactions(Array.isArray(transactionsData) ? transactionsData : []);
    } catch (err) {
      console.error('Error loading transaction history:', err);
      setError(err instanceof Error ? err.message : 'Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatNumber = (num: number): string => {
    return new Intl.NumberFormat().format(num);
  };

  const getFeatureIcon = (featureType: string): string => {
    if (!featureType) return '📊';
    const iconMap: Record<string, string> = {
      CAPTION: '📝',
      THUMBNAIL: '🖼️',
      VOICE_SYNTHESIS: '🎤',
      MUSIC_GENERATION: '🎵',
      TITLE_DESCRIPTION: '✍️',
      IMAGE_ANALYSIS: '🔍',
      IMAGE_GENERATION: '🎨',
      VIDEO_GENERATION: '🎬',
      PROMPT: '💡',
      SCRIPT: '📄',
      PURCHASE: '💳',
      REFUND: '↩️',
      ADMIN_ADJUSTMENT: '⚙️',
    };
    return iconMap[featureType] || '📊';
  };

  const getFeatureColor = (points: number): string => {
    if (points > 0) return '#10b981'; // Green for additions
    return '#00d4ff'; // Cyan for usage
  };

  const getTransactionSign = (points: number): string => {
    return points > 0 ? '+' : '';
  };

  const formatFeatureType = (featureType: string): string => {
    if (!featureType) return 'Unknown';
    return featureType
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h2 className={styles.title}>POINTS TRANSACTION LOG</h2>
            <p className={styles.subtitle}>
              Complete history of your points transactions
            </p>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <span>×</span>
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
              <span className={styles.loadingText}>Accessing transaction records...</span>
            </div>
          ) : error ? (
            <div className={styles.errorContainer}>
              <span className={styles.errorIcon}>⚠️</span>
              <span className={styles.errorText}>{error}</span>
              <button className={styles.retryButton} onClick={loadTransactions}>
                Retry
              </button>
            </div>
          ) : transactions.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📊</div>
              <h3 className={styles.emptyTitle}>No Transactions Found</h3>
              <p className={styles.emptyText}>
                Your points transaction history will appear here once you start using features.
              </p>
            </div>
          ) : (
            <div className={styles.transactionsList}>
              {transactions.map((transaction) => (
                <div key={transaction.id} className={styles.transactionItem}>
                  <div className={styles.transactionLeft}>
                    <div 
                      className={styles.transactionIcon}
                      style={{ backgroundColor: getFeatureColor(transaction.points) }}
                    >
                      {getFeatureIcon(transaction.featureType)}
                    </div>
                    <div className={styles.transactionInfo}>
                      <div className={styles.transactionDescription}>
                        {transaction.description || formatFeatureType(transaction.featureType)}
                      </div>
                      <div className={styles.transactionMeta}>
                        <span className={styles.transactionDate}>
                          {formatDate(transaction.createdAt)}
                        </span>
                        <span className={styles.featureType}>
                          • {formatFeatureType(transaction.featureType)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.transactionRight}>
                    <div 
                      className={styles.pointsChange}
                      style={{ color: getFeatureColor(transaction.points || 0) }}
                    >
                      {getTransactionSign(transaction.points || 0)}{Math.abs(transaction.points || 0)}
                    </div>
                    <div className={styles.balanceAfter}>
                      Balance: {transaction.balanceAfter !== undefined && transaction.balanceAfter !== null 
                        ? formatNumber(transaction.balanceAfter) 
                        : 'N/A'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerInfo}>
            <span className={styles.infoIcon}>ℹ️</span>
            <span>Transaction records are kept for your account history</span>
          </div>
          <button className={styles.closeButtonFooter} onClick={onClose}>
            Close
          </button>
        </div>

        {/* Futuristic Effects */}
        <div className={styles.modalEffects}>
          <div className={styles.scanLine}></div>
          <div className={styles.dataGrid}></div>
        </div>
      </div>
    </div>
  );
};

export default PointsHistoryModal;
